import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { apiUrl } from "../config/api";

export default function CalcJournal() {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [objects, setObjects] = useState([]); // для копирования
  const [copyOpen, setCopyOpen] = useState(false);
  const [copyTarget, setCopyTarget] = useState("");
  const [copySearch, setCopySearch] = useState("");
  const [filters, setFilters] = useState({
    objectCode: "",
    objectName: "",
    address: "",
    contractType: "",
    period: "",
    status: "",
    comment: "",
  });
  const [onlyActual, setOnlyActual] = useState(false);
  const [highlightedId, setHighlightedId] = useState(null);
  const [searchResults, setSearchResults] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState("");
  const navigate = useNavigate();

  const openCardInNewTab = useCallback((objectCode) => {
    if (!objectCode) return;
    const url = `/card-calc/${encodeURIComponent(objectCode)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  }, []);

  const loadCards = useCallback(() => {
    setLoading(true);
    setError("");
    fetch(apiUrl('/api/card-calc?page=1&perPage=300&full=1'))
      .then((res) => res.json())
      .then((data) => {
        const arr = Array.isArray(data) ? data : Array.isArray(data?.items) ? data.items : [];
        const limited = arr.slice(0, 300).sort((a, b) => Number(b.objectCode) - Number(a.objectCode));
        setCards(limited);
        setLoading(false);
      })
      .catch((e) => {
        setError("Ошибка загрузки данных");
        setLoading(false);
      });
  }, []);

  const hasFilterInput = useMemo(() => {
    return Object.values(filters).some((v) => String(v || "").trim() !== "");
  }, [filters]);
  const shouldUseSearch = hasFilterInput || onlyActual;

  useEffect(() => {
    loadCards();
  }, [loadCards]);

  useEffect(() => {
    if (!shouldUseSearch) {
      setSearchResults(null);
      setSearchLoading(false);
      setSearchError("");
      return;
    }

    const controller = new AbortController();
    const params = new URLSearchParams();
    params.set('limit', '2000');
    Object.entries(filters).forEach(([key, value]) => {
      const trimmed = String(value || '').trim();
      if (trimmed) params.append(key, trimmed);
    });
    if (onlyActual) params.append('onlyActual', '1');

    setSearchLoading(true);
    setSearchError("");
    fetch(apiUrl(`/api/card-calc/search?${params.toString()}`), { signal: controller.signal })
      .then((res) => {
        if (!res.ok) throw new Error(`SEARCH_HTTP_${res.status}`);
        return res.json();
      })
      .then((data) => {
        const arr = Array.isArray(data) ? data : [];
        arr.sort((a, b) => Number(b.objectCode) - Number(a.objectCode));
        setSearchResults(arr);
        setSearchLoading(false);
      })
      .catch((err) => {
        if (err.name === 'AbortError') return;
        setSearchError('Ошибка применения фильтра');
        setSearchLoading(false);
      });

    return () => controller.abort();
  }, [filters, onlyActual, shouldUseSearch]);

  // удалён выбор объекта при копировании — теперь просто подтверждение

  function handleDelete(objectCode) {
    if (!window.confirm("Удалить карточку?")) return;
    fetch(apiUrl(`/api/card-calc/${objectCode}`), { method: "DELETE" })
      .then((res) => res.json())
      .then((result) => {
        if (result.ok) {
          setCards((cards) => cards.filter((c) => String(c.objectCode) !== String(objectCode)));
          setSearchResults((list) => (Array.isArray(list) ? list.filter((c) => String(c.objectCode) !== String(objectCode)) : list));
          if (String(highlightedId) === String(objectCode)) setHighlightedId(null);
        } else {
          alert("Ошибка удаления: " + (result.error || ""));
        }
      })
      .catch((err) => alert("Ошибка запроса: " + err));
  }

  function handleCreate() {
    navigate("/card-calc/new");
  }

  // открыть модал копирования: подгружаем объекты один раз
  const openCopyModal = () => {
    if (!highlightedId) return;
    if (!objects.length) {
      fetch(apiUrl('/api/objects'))
        .then((r) => r.json())
        .then((arr) => setObjects(Array.isArray(arr) ? arr : []))
        .catch(() => setObjects([]));
    }
    setCopySearch("");
    setCopyTarget("");
    setCopyOpen(true);
  };

  const filteredObjects = useMemo(() => {
    const q = (copySearch || "").toLowerCase().trim();
    if (!q) return objects;
    return objects.filter((o) =>
      String(o.codeBase).includes(q) || String(Number(o.codeBase)).includes(q) || String(o.name || "").toLowerCase().includes(q)
    );
  }, [copySearch, objects]);

  const doCopy = () => {
    if (!highlightedId) return;
    const targetId = copyTarget ? Number(copyTarget) : null;
    if (!targetId) { alert("Выберите объект для копирования"); return; }
    fetch(apiUrl(`/api/card-calc/${encodeURIComponent(highlightedId)}/copy`), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ targetObjectId: targetId })
    })
      .then((r) => r.json())
      .then((created) => {
        setCopyOpen(false);
        if (created && created.objectCode) navigate(`/card-calc/${created.objectCode}`);
        else loadCards();
      })
      .catch((err) => alert("Ошибка копирования: " + err));
  };

  // фильтрованная версия списка по inputs
  const filtered = useMemo(() => {
    const source = shouldUseSearch ? (searchResults ?? cards) : cards;
    const list = Array.isArray(source) ? source : [];
    const f = {
      code: (filters.objectCode || "").toString().toLowerCase(),
      name: (filters.objectName || "").toLowerCase(),
      address: (filters.address || "").toLowerCase(),
      ctype: (filters.contractType || "").toLowerCase(),
      period: (filters.period || "").toLowerCase(),
      status: (filters.status || "").toLowerCase(),
      comment: (filters.comment || "").toLowerCase(),
    };
    return list.filter((card) => {
      const codeStr = String(card.objectCode || "").toLowerCase();
      const codeFull = String(card.objectCodeFull || "").toLowerCase();
      const codeNoZeros = String(Number(card.objectCode || "")).toLowerCase();
      const isActual = !!card.actualAt;
      const isSigned = !!card.signedAt;
      const statusText = isActual ? 'актуален' : (isSigned ? 'согласован' : 'черновик');
      const periodText = (card.periodMonth && card.periodYear) ? `${String(card.periodMonth).padStart(2,'0')}.${card.periodYear}`.toLowerCase() : '';
      const ctypeText = (card.contractType === 'one-time' ? 'разовый' : 'стационар').toLowerCase();
      return (
        (f.code === "" || codeStr.includes(f.code) || codeFull.includes(f.code) || codeNoZeros.includes(f.code)) &&
        (f.name === "" || String(card.objectName || "").toLowerCase().includes(f.name)) &&
        (f.address === "" || String(card.address || "").toLowerCase().includes(f.address)) &&
        (f.ctype === "" || ctypeText.includes(f.ctype)) &&
        (f.period === "" || periodText.includes(f.period)) &&
        (f.status === "" || statusText.includes(f.status)) &&
        (f.comment === "" || String(card.comment || "").toLowerCase().includes(f.comment))
      ) && (!onlyActual || isActual);
    });
  }, [cards, searchResults, filters, onlyActual, shouldUseSearch]);

  const onFilterChange = (key, value) => {
    setFilters((s) => ({ ...s, [key]: value }));
  };

  const fillFilterByCell = (key, rawValue, e) => {
    if (e && e.ctrlKey) {
      e.stopPropagation();
      const v = String(rawValue ?? '').trim();
      setFilters((s) => ({ ...s, [key]: v }));
      return true;
    }
    return false;
  };

  const journalCardStyle = {
    background: "var(--bg-card, #fff)",
    borderRadius: 20,
    border: "1px solid var(--border-200, #e6e8ef)",
    boxShadow: "var(--shadow-1, 0 22px 44px rgba(15,23,42,0.08))",
    overflowX: "auto",
  };
  const journalTableStyle = {
    borderCollapse: "separate",
    borderSpacing: 0,
    width: "100%",
    tableLayout: "fixed",
    borderRadius: 18,
    overflow: "hidden",
    fontSize: "15px",
  };
  const thBase = {
    border: "1px solid var(--border-200, #e6e8ef)",
    padding: "10px 8px",
    background: "linear-gradient(135deg, var(--brand-50, #f5efff), #f3f4f8)",
    fontWeight: 700,
    textAlign: "left",
    color: "var(--text-700, #2b2f36)",
    whiteSpace: "normal",
  };
  const filterTh = {
    ...thBase,
    background: "#fdfdff",
    fontWeight: 500,
  };
  const tdBase = {
    border: "1px solid var(--border-200, #e6e8ef)",
    padding: "10px 8px",
    background: "#fff",
    textAlign: "left",
    whiteSpace: "normal",
    wordBreak: "break-word",
  };
  const filterInputStyle = { width: "100%", padding: "6px 8px", fontSize: 13, borderRadius: 8, border: "1px solid #cfd4e1", boxSizing: "border-box", minWidth: 0 };
  const rowBaseStyle = {
    transition: "background 0.18s ease, box-shadow 0.18s ease",
    cursor: "pointer",
  };
  const selectedRowExtras = {
    background: "rgba(86,61,255,0.12)",
    boxShadow: "inset 0 0 0 2px rgba(86,61,255,0.35)",
    outline: "2px solid rgba(86,61,255,0.35)",
    outlineOffset: -2,
  };
  const toolbarButtonBase = {
    padding: '8px 14px',
    borderRadius: 8,
    border: '1px solid #cbd5f5',
    background: '#f8fafc',
    fontWeight: 600,
    fontSize: 13,
    color: '#0f172a',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    boxShadow: '0 1px 2px rgba(15,23,42,0.15)'
  };
  const toolbarButtonActive = {
    background: '#1d4ed8',
    color: '#fff',
    borderColor: '#1d4ed8',
    boxShadow: '0 6px 18px rgba(37,99,235,0.35)'
  };
  const toolbarPrimaryButton = {
    padding: '8px 14px',
    borderRadius: 8,
    border: '1px solid #4caf50',
    background: '#e0f7e9',
    fontWeight: 600,
    fontSize: 14,
    color: '#065f46',
    cursor: 'pointer',
    transition: 'box-shadow 0.15s ease',
    boxShadow: '0 4px 14px rgba(5,150,105,0.25)'
  };
  const toolbarCopyButton = {
    ...toolbarButtonBase,
    background: '#dbeafe',
    borderColor: '#2563eb',
    color: '#1e3a8a'
  };
  const toolbarDisabled = {
    opacity: 0.55,
    cursor: 'not-allowed',
    boxShadow: 'none'
  };

  const renderStatusBadge = (statusText) => {
    const base = {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "4px 10px",
      borderRadius: 999,
      fontWeight: 700,
      fontSize: 13,
      textTransform: "none",
      width: "100%",
      boxSizing: "border-box",
    };
    if (statusText === 'Актуален') return { ...base, background: "#e0edff", color: "#1d4ed8" };
    if (statusText === 'Согласован') return { ...base, background: "#dcfce7", color: "#15803d" };
    return { ...base, background: "#fef3c7", color: "#92400e" };
  };

  return (
    <div style={{ padding: "32px 0 0 0" }}>
      <h2 style={{ textAlign: "center", margin: "0 0 14px 0" }}>Журнал расчетных карточек</h2>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button
            onClick={() => setOnlyActual((v) => !v)}
            style={{
              ...toolbarButtonBase,
              ...(onlyActual ? toolbarButtonActive : {})
            }}
            title="Показывать только актуальные карточки"
          >{onlyActual ? 'Только актуальные: ВКЛ' : 'Только актуальные'}</button>
        </div>
        <button
          style={toolbarPrimaryButton}
          onClick={handleCreate}
        >
          ➕ Новая карточка
        </button>
        <button
          style={{
            ...toolbarCopyButton,
            ...(highlightedId ? {} : toolbarDisabled),
            marginLeft: 8
          }}
          onClick={openCopyModal}
          disabled={!highlightedId}
          title={highlightedId ? "Копировать выбранную…" : "Сначала выберите карточку в таблице"}
        >
          📄 Копировать…
        </button>
      </div>

      {shouldUseSearch && searchLoading && (
        <div style={{ textAlign: "center", color: "#475569", marginBottom: 8 }}>Применяем фильтр…</div>
      )}
      {searchError && (
        <div style={{ textAlign: "center", color: "#c00", marginBottom: 8 }}>{searchError}</div>
      )}

      {loading ? (
        <div style={{ textAlign: "center" }}>Загрузка...</div>
      ) : error ? (
        <div style={{ textAlign: "center", color: "#c00" }}>{error}</div>
      ) : (
        <>
          <div style={journalCardStyle} className="journal-card">
            <table className="calc-table journal-table" style={journalTableStyle}>
            <thead>
              <tr>
                <th style={{ ...thBase, width: "11%" }}>Код</th>
                <th style={{ ...thBase, width: "27%" }}>Название объекта</th>
                <th style={{ ...thBase, width: "17%" }}>Адрес</th>
                <th style={{ ...thBase, width: "10%" }}>Вид контракта</th>
                <th style={{ ...thBase, width: "9%" }}>Период</th>
                <th style={{ ...thBase, width: "12%" }}>Статус</th>
                <th style={{ ...thBase, width: "14%" }}>Комментарий</th>
                <th style={{ ...thBase, width: "10%" }}></th>
              </tr>

              <tr>
                <th style={filterTh}>
                  <input
                    value={filters.objectCode}
                    onChange={(e) => onFilterChange("objectCode", e.target.value)}
                    placeholder="Поиск..."
                    style={filterInputStyle}
                  />
                </th>
                <th style={filterTh}>
                  <input
                    value={filters.objectName}
                    onChange={(e) => onFilterChange("objectName", e.target.value)}
                    placeholder="Поиск..."
                    style={filterInputStyle}
                  />
                </th>
                <th style={filterTh}>
                  <input
                    value={filters.address}
                    onChange={(e) => onFilterChange("address", e.target.value)}
                    placeholder="Поиск..."
                    style={filterInputStyle}
                  />
                </th>
                <th style={filterTh}>
                  <input
                    value={filters.contractType}
                    onChange={(e) => onFilterChange("contractType", e.target.value)}
                    placeholder="Поиск..."
                    style={filterInputStyle}
                  />
                </th>
                <th style={filterTh}>
                  <input
                    value={filters.period}
                    onChange={(e) => onFilterChange("period", e.target.value)}
                    placeholder="мм.гггг"
                    style={filterInputStyle}
                  />
                </th>
                <th style={filterTh}>
                  <input
                    value={filters.status}
                    onChange={(e) => onFilterChange("status", e.target.value)}
                    placeholder="Черновик/Согласован/Актуален"
                    style={filterInputStyle}
                  />
                </th>
                <th style={filterTh}>
                  <input
                    value={filters.comment}
                    onChange={(e) => onFilterChange("comment", e.target.value)}
                    placeholder="Поиск..."
                    style={filterInputStyle}
                  />
                </th>
                <th style={filterTh} />
              </tr>
            </thead>

            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={9} style={{ ...tdBase, textAlign: "center", color: "#888" }}>
                    Нет карточек
                  </td>
                </tr>
              ) : (
                filtered.map((card) => {
                  const isSelected = String(card.objectCode) === String(highlightedId);
                  const isActual = !!card.actualAt;
                  const isSigned = !!card.signedAt;
                  const statusText = isActual ? 'Актуален' : (isSigned ? 'Согласован' : 'Черновик');
                  const rowBg = isActual ? '#e7f0ff' : (isSigned ? '#e7f6d4' : 'transparent');
                  const period = (card.periodMonth && card.periodYear) ? `${String(card.periodMonth).padStart(2,'0')}.${card.periodYear}` : '—';
                  const ctype = card.contractType === 'one-time' ? 'Разовый' : 'Стационар';
                  const detailPath = `/card-calc/${encodeURIComponent(card.objectCode)}`;
                  return (
                    <tr
                      key={card.objectCode}
                      onDoubleClick={() => openCardInNewTab(card.objectCode)}
                      onClick={(e) => { if (e.ctrlKey) return; setHighlightedId(card.objectCode); }}
                      style={{
                        ...rowBaseStyle,
                        cursor: "pointer",
                        background: rowBg,
                        ...(isSelected ? selectedRowExtras : {}),
                      }}
                    >
                      <td style={tdBase} onClick={(e)=>{ if (fillFilterByCell('objectCode', card.objectCodeFull || `${String(card.objectCode).padStart(4,'0')}-0`, e)) return; }}>
                        <Link to={detailPath} target="_blank" rel="noreferrer">
                          {card.objectCodeFull || `${String(card.objectCode).padStart(4,'0')}-0`}
                        </Link>
                      </td>
                      <td style={tdBase} onClick={(e)=>fillFilterByCell('objectName', card.objectName, e)}>{card.objectName}</td>
                      <td style={tdBase} onClick={(e)=>fillFilterByCell('address', card.address, e)}>{card.address}</td>
                      <td style={tdBase} onClick={(e)=>fillFilterByCell('contractType', ctype, e)}>{ctype}</td>
                      <td style={tdBase} onClick={(e)=>fillFilterByCell('period', period, e)}>{period}</td>
                      <td style={tdBase} onClick={(e)=>fillFilterByCell('status', statusText, e)}>
                        <span style={renderStatusBadge(statusText)}>{statusText}</span>
                      </td>
                      <td style={tdBase} onClick={(e)=>fillFilterByCell('comment', card.comment, e)}>{card.comment}</td>
                      <td style={{ ...tdBase, textAlign: "center" }}>
                        <button
                          style={{
                            border: "none",
                            background: "none",
                            color: "#c00",
                            fontSize: "18px",
                            cursor: "pointer",
                          }}
                          onClick={() => handleDelete(card.objectCode)}
                          title="Удалить карточку"
                        >
                          🗑️
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
            </table>
          </div>
          <div style={{ marginTop: 10, fontSize: 13, color: '#475569', textAlign: 'right' }}>
            Строк: <strong>{filtered.length}</strong>
          </div>
        </>
      )}

      {/* Модал копирования */}
      {copyOpen && (
        <div style={{ position: "fixed", inset: 0, background: "#0008", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999 }}
             onClick={() => setCopyOpen(false)}>
          <div style={{ background: "#fff", padding: 18, borderRadius: 8, width: 520, maxWidth: "90%" }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginTop: 0 }}>Копировать карточку</h3>
            <div style={{ marginBottom: 8 }}>Выберите объект назначения:</div>
            <input
              placeholder="Поиск по коду или названию"
              value={copySearch}
              onChange={(e) => setCopySearch(e.target.value)}
              style={{ width: "100%", padding: "8px 10px", marginBottom: 8, border: "1px solid #ccc", borderRadius: 6 }}
            />
            <select
              value={copyTarget}
              onChange={(e) => setCopyTarget(e.target.value)}
              style={{ width: "100%", padding: "8px 10px", border: "1px solid #ccc", borderRadius: 6, maxHeight: 180 }}
            >
              <option value="">— выберите объект —</option>
              {filteredObjects.map((o) => (
                <option key={o.id} value={o.id}>{Number(o.codeBase)} — {o.name}</option>
              ))}
            </select>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 14 }}>
              <button onClick={() => setCopyOpen(false)} style={{ padding: "8px 12px" }}>Отмена</button>
              <button onClick={doCopy} disabled={!copyTarget} style={{ padding: "8px 12px", background: "#e7f6d4", border: "1px solid #8bc34a", borderRadius: 6 }}>Скопировать</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}